/**
 * Computes a proper two-sided diff between `original` and `edited`.
 * Returns `{ before, after }` containing **only** the keys whose values
 * actually changed.  Handles primitives, nested objects, and arrays
 * (arrays are compared via JSON serialisation).
 *
 * Use this when you need both sides of the diff — e.g. to feed into
 * `JsonBeforeAfter` after a user edits data:
 *
 * ```ts
 * const { before, after } = computeChanges(originalData, editedData)
 * // before = { name: 'Main Office' }
 * // after  = { name: 'Branch Office' }
 * ```
 *
 * Compare with `extractOverlap`, which only returns the *source* side
 * for overlapping keys (designed for change-request review flows where
 * the change request itself is the "after").
 *
 * **Safety**: Guards against circular references (bails out at 20 levels
 * of nesting) and only iterates own enumerable properties.
 *
 * @param original - The original / baseline object
 * @param edited   - The edited / proposed object
 * @returns An object with `before` and `after` containing only changed keys
 */

const MAX_DEPTH = 20

/** Safe JSON.stringify that returns a fallback on circular refs. */
const safeStringify = (value: unknown): string => {
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

export const computeChanges = (
  original: Record<string, unknown>,
  edited: Record<string, unknown>,
  depth = 0
): { before: Record<string, unknown>; after: Record<string, unknown> } => {
  const before: Record<string, unknown> = {}
  const after: Record<string, unknown> = {}

  // Bail out at extreme nesting to prevent stack overflow on circular refs
  if (depth >= MAX_DEPTH) {
    const origStr = safeStringify(original)
    const editStr = safeStringify(edited)
    if (origStr !== editStr) {
      return { before: original, after: edited }
    }
    return { before: {}, after: {} }
  }

  const editedKeys = Object.keys(edited)
  for (let i = 0; i < editedKeys.length; i++) {
    const key = editedKeys[i]

    if (Object.prototype.hasOwnProperty.call(original, key)) {
      const origVal = original[key]
      const editVal = edited[key]

      if (Array.isArray(origVal) && Array.isArray(editVal)) {
        if (safeStringify(origVal) !== safeStringify(editVal)) {
          before[key] = origVal
          after[key] = editVal
        }
      } else if (
        typeof origVal === 'object' &&
        origVal !== null &&
        typeof editVal === 'object' &&
        editVal !== null
      ) {
        const nested = computeChanges(
          origVal as Record<string, unknown>,
          editVal as Record<string, unknown>,
          depth + 1
        )
        if (
          Object.keys(nested.before).length > 0 ||
          Object.keys(nested.after).length > 0
        ) {
          before[key] = nested.before
          after[key] = nested.after
        }
      } else if (origVal !== editVal) {
        before[key] = origVal
        after[key] = editVal
      }
    } else {
      // Key was added
      after[key] = edited[key]
    }
  }

  // Keys that were removed
  const originalKeys = Object.keys(original)
  for (let i = 0; i < originalKeys.length; i++) {
    const key = originalKeys[i]
    if (!Object.prototype.hasOwnProperty.call(edited, key)) {
      before[key] = original[key]
    }
  }

  return { before, after }
}
